import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { EcommerceService } from 'src/app/services/ecommerce.service';
import { GlobalCategoryService } from 'src/app/services/global-category.service';
import { BreeceService } from 'src/app/services/breece.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { forkJoin, of, Subscription } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Component({
  selector: 'app-store-config',
  templateUrl: './store-config.component.html',
  styleUrls: ['./store-config.component.scss']
})
export class StoreConfigComponent implements OnInit, OnChanges, OnDestroy {
  @Input() storeId!: string;
  @Input() storeConfig: any;

  private formDirtySub?: Subscription;
  /** Incremented on store switch to ignore in-flight HTTP callbacks from the previous store. */
  private storeContextSeq = 0;

  // Form and UI State
  activeTab: string = 'basic';
  storeSettingsForm!: FormGroup;
  loading: boolean = false;
  saving: boolean = false;
  hasUnsavedChanges: boolean = false;

  // Store Data
  storeSettings: any = {};
  storeCategories: any[] = [];
  storeCategoriesLoaded = false;

  globalCategoriesFlat: any[] = [];
  globalCategoryMappingRows: { globalCategoryId: string; name: string; depth: number; path: string; storeCategoryId: string }[] = [];
  globalCategoryMappingSaving = false;
  globalCategoryMappingLoadError: string | null = null;

  // Breece ESL Integration
  showBreeceSecret = false;
  testingBreece = false;
  breeceSaving: boolean = false;
  breeceIntervalSaving: boolean = false;
  breeceManualSyncing: boolean = false;
  breeceTestResult: { success: boolean; message: string } | null = null;
  breeceInstallations: {id: number, name: string}[] = [];
  lastSuccessfulSyncAt: string | null = null;

  // TOMRA Voucher Configuration
  tomraVouchers: {plu: string, name: string}[] = [];
  tomraSaving: boolean = false;
  tomraConnectionStatus: string | null = null;
  tomraConnectionLoading = false;

  // Auto Z-Report
  autoZReportEnabled: boolean = false;
  autoZReportScheduledTime: string = '23:00';
  autoZReportLastRunAt: Date | null = null;
  autoZReportLastRunStatus: string | null = null;
  autoZReportLastRunError: string | null = null;
  autoZReportHistory: Array<{executedAt: string; status: string; reportId: string | null; error: string | null}> = [];
  autoZReportSaving: boolean = false;

  // Deposit fee types (global catalog)
  depositFeeTypes: any[] = [];
  // Per-store overrides: { feeTypeId, name, description, defaultAmount, amount, enabled }
  storeFeeTypeEntries: any[] = [];

  // Currency Options
  currencyOptions = [
    { value: 'DKK', label: 'Danish Kroner (DKK)', symbol: 'kr' },
    { value: 'EUR', label: 'Euro (EUR)', symbol: '€' },
    { value: 'USD', label: 'US Dollar (USD)', symbol: '$' },
    { value: 'GBP', label: 'British Pound (GBP)', symbol: '£' },
    { value: 'SEK', label: 'Swedish Krona (SEK)', symbol: 'kr' },
    { value: 'NOK', label: 'Norwegian Krone (NOK)', symbol: 'kr' }
  ];

  constructor(
    private ecommerceService: EcommerceService,
    private globalCategoryService: GlobalCategoryService,
    private breeceService: BreeceService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService
  ) { }

  ngOnInit(): void {
    this.initializeForm();
    this.bindFormDirtyTracking();
    if (this.storeId) {
      this.loadAll();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    const storeIdChange = changes['storeId'];
    if (!storeIdChange || storeIdChange.firstChange) {
      return;
    }
    const prev = storeIdChange.previousValue;
    const curr = storeIdChange.currentValue;
    if (prev !== curr && curr) {
      this.resetPerStoreState();
      this.loadAll();
    }
  }

  ngOnDestroy(): void {
    this.formDirtySub?.unsubscribe();
  }

  /** Clear all per-store transient state before loading a different store. */
  private resetPerStoreState(): void {
    this.storeContextSeq++;
    this.activeTab = 'basic';
    this.loading = false;
    this.saving = false;
    this.hasUnsavedChanges = false;

    this.storeSettings = {};
    this.storeCategories = [];
    this.storeCategoriesLoaded = false;
    this.globalCategoriesFlat = [];
    this.globalCategoryMappingRows = [];
    this.globalCategoryMappingSaving = false;
    this.globalCategoryMappingLoadError = null;
    this.depositFeeTypes = [];
    this.storeFeeTypeEntries = [];

    this.showBreeceSecret = false;
    this.testingBreece = false;
    this.breeceSaving = false;
    this.breeceIntervalSaving = false;
    this.breeceManualSyncing = false;
    this.breeceTestResult = null;
    this.breeceInstallations = [];
    this.lastSuccessfulSyncAt = null;

    this.tomraVouchers = [];
    this.tomraSaving = false;
    this.tomraConnectionStatus = null;
    this.tomraConnectionLoading = false;

    this.autoZReportEnabled = false;
    this.autoZReportScheduledTime = '23:00';
    this.autoZReportLastRunAt = null;
    this.autoZReportLastRunStatus = null;
    this.autoZReportLastRunError = null;
    this.autoZReportHistory = [];
    this.autoZReportSaving = false;

    this.initializeForm();
    this.bindFormDirtyTracking();
  }

  private bindFormDirtyTracking(): void {
    this.formDirtySub?.unsubscribe();
    this.formDirtySub = this.storeSettingsForm.valueChanges.subscribe(() => {
      this.hasUnsavedChanges = this.storeSettingsForm.dirty;
    });
  }

  loadAll(): void {
    const seq = this.storeContextSeq;
    this.loading = true;
    this.spinner.show();
    forkJoin({
      settings: this.ecommerceService.getStoreSettings(this.storeId),
      feeTypes: this.ecommerceService.getDepositFeeTypes().pipe(catchError(() => of(null))),
      categories: this.ecommerceService.getStoreCategories(this.storeId, { adminView: 'true' }).pipe(catchError(() => of(null))),
      globalCategories: this.globalCategoryService.listCategories({ status: '1' }).pipe(catchError(() => of(null))),
      globalMappings: this.globalCategoryService.getStoreMappings(this.storeId).pipe(catchError(() => of(null)))
    }).subscribe(
      ({ settings, feeTypes, categories, globalCategories, globalMappings }: any) => {
        if (seq !== this.storeContextSeq) {
          return;
        }
        this.loading = false;
        this.spinner.hide();

        // Parse global fee types (null if fee catalog API failed — store settings still load)
        if (feeTypes === null) {
          this.toastr.warning('Could not load deposit fee catalog. Store settings will still load.');
        }
        const rawTypes = feeTypes?.response?.data ?? feeTypes?.data ?? feeTypes;
        this.depositFeeTypes = Array.isArray(rawTypes) ? rawTypes.filter((ft: any) => ft.active) : [];

        this.storeCategoriesLoaded = false;
        if (categories === null) {
          this.storeCategories = [];
          this.toastr.warning('Could not load store categories. Default category for catalog imports cannot be changed right now.');
        } else if (categories.response?.success && categories.response?.data) {
          this.storeCategories = categories.response.data.categories || [];
          this.storeCategoriesLoaded = true;
        } else if (categories.code === 200 && categories.data) {
          this.storeCategories = categories.data.categories || [];
          this.storeCategoriesLoaded = true;
        } else {
          this.storeCategories = [];
          this.toastr.warning('Could not load store categories. Default category for catalog imports cannot be changed right now.');
        }

        // Parse store settings
        let store: any = null;
        if (settings.code === 200 && settings.data?.store) {
          store = settings.data.store;
        } else if (settings.response?.success && settings.response?.data?.store) {
          store = settings.response.data.store;
        }

        if (store) {
          this.storeSettings = store;
          this.populateForm(store);
        } else {
          this.toastr.error('Failed to load store settings');
        }

        this.applyGlobalCategoryMappingLoad(globalCategories, globalMappings);
      },
      (error: any) => {
        if (seq !== this.storeContextSeq) {
          return;
        }
        this.loading = false;
        this.spinner.hide();
        console.error('Error loading store config data:', error);

        // Fallback: use depositSettings from parent-provided storeConfig (works for managers)
        if (this.storeConfig?.depositSettings) {
          console.log('Using storeConfig fallback for store settings');
          this.storeSettings = this.storeConfig;
          this.populateForm(this.storeConfig);
        } else {
          this.toastr.error('Failed to load store settings');
        }
      }
    );
  }

  private initializeForm(): void {
    this.storeSettingsForm = new FormGroup({
      // Basic Settings
      storeName: new FormControl('', [Validators.required, Validators.minLength(2)]),
      currency: new FormControl('DKK', [Validators.required]),
      commissionRate: new FormControl(5, [Validators.required, Validators.min(0), Validators.max(100)]),
      payoutSchedule: new FormControl('monthly'),
      isEcommerceEnabled: new FormControl(true),
      inventoryManagementEnabled: new FormControl(false),
      skuRequirementEnabled: new FormControl(false),
      catalogImportDefaultCategoryId: new FormControl(''),
      
      // Bank Details
      bankAccountNumber: new FormControl(''),
      bankName: new FormControl(''),
      swiftCode: new FormControl(''),
      bankAccountHolderName: new FormControl(''),
      
      // Tax Information
      vatNumber: new FormControl(''),
      taxId: new FormControl(''),
      businessRegistration: new FormControl(''),
      
      // Deposit Settings
      depositEnabled: new FormControl(false),
      depositAutoAssignByEAN: new FormControl(false),
      // Legacy backward-compat fields (read from API, not shown in UI)
      depositCategoryA: new FormControl(1.00, [Validators.min(0)]),
      depositCategoryB: new FormControl(1.50, [Validators.min(0)]),
      depositCategoryC: new FormControl(3.00, [Validators.min(0)]),

      // Breece ESL Integration
      breeceConfig: new FormGroup({
        enabled:              new FormControl(false),
        clientId:             new FormControl(''),
        clientSecret:         new FormControl(''),
        installationId:       new FormControl(null),
        syncIntervalMinutes:  new FormControl(10)
      }),

      // TOMRA Voucher Configuration
      tomraConfig: new FormGroup({
        enabled:      new FormControl(false),
        mode:         new FormControl('offline'),
        clientId:     new FormControl(''),
        clientSecret: new FormControl(''),
        apiBaseUrl:   new FormControl(''),
        realm:        new FormControl('')
      })
    });
  }

  loadStoreSettings(): void {
    this.loadAll();
  }

  private populateForm(settings: any): void {
    this.storeSettingsForm.patchValue({
      storeName: settings.storeName || '',
      currency: settings.currency || 'DKK',
      commissionRate: settings.commissionRate ?? 5,
      payoutSchedule: settings.payoutSchedule || 'monthly',
      isEcommerceEnabled: settings.isEcommerceEnabled !== false,
      inventoryManagementEnabled: settings.inventoryManagementEnabled || false,
      skuRequirementEnabled: settings.skuRequirementEnabled || false,
      catalogImportDefaultCategoryId: settings.catalogImportDefaultCategoryId || '',
      bankAccountNumber: settings.bankDetails?.accountNumber || '',
      bankName: settings.bankDetails?.bankName || '',
      swiftCode: settings.bankDetails?.swiftCode || '',
      bankAccountHolderName: settings.bankDetails?.accountHolderName || '',
      vatNumber: settings.taxInfo?.vatNumber || '',
      taxId: settings.taxInfo?.taxId || '',
      businessRegistration: settings.taxInfo?.businessRegistration || '',
      depositEnabled: settings.depositSettings?.enabled || false,
      depositAutoAssignByEAN: settings.depositSettings?.autoAssignByEAN || false,
      // Legacy backward-compat
      depositCategoryA: settings.depositSettings?.categoryA || 1.00,
      depositCategoryB: settings.depositSettings?.categoryB || 1.50,
      depositCategoryC: settings.depositSettings?.categoryC || 3.00
    });

    // Build per-store fee type entries from global catalog
    const storeOverrides: any[] = settings.depositSettings?.feeTypes || [];
    this.storeFeeTypeEntries = this.depositFeeTypes.map((globalFt: any) => {
      const override = storeOverrides.find((o: any) => {
        const oId = typeof o.feeTypeId === 'object' ? o.feeTypeId._id : o.feeTypeId;
        return oId === globalFt._id;
      });
      return {
        feeTypeId: globalFt._id,
        name: globalFt.name,
        description: globalFt.description,
        defaultAmount: globalFt.defaultAmount,
        providerCode: globalFt.providerCode || '',
        eanRangeStart: globalFt.eanRangeStart,
        eanRangeEnd: globalFt.eanRangeEnd,
        amount: override?.amount ?? globalFt.defaultAmount,
        enabled: override?.enabled ?? true
      };
    });

    this.storeSettingsForm.markAsPristine();
    this.hasUnsavedChanges = false;

    // Patch Breece config (clientSecret is not returned by GET)
    if (settings.breeceConfig) {
      this.storeSettingsForm.patchValue({ breeceConfig: {
        enabled:             settings.breeceConfig.enabled || false,
        clientId:            settings.breeceConfig.clientId || '',
        installationId:      settings.breeceConfig?.installationId != null
          ? this.normalizeInstallationId(settings.breeceConfig.installationId)
          : null,
        syncIntervalMinutes: settings.breeceConfig?.syncIntervalMinutes ?? 10
      }});
      this.lastSuccessfulSyncAt = settings.breeceConfig?.lastSuccessfulSyncAt ?? null;
    }

    // Patch TOMRA config
    if (settings.tomraConfig) {
      this.storeSettingsForm.patchValue({ tomraConfig: {
        enabled:    settings.tomraConfig.enabled || false,
        mode:       settings.tomraConfig.mode || 'offline',
        clientId:   settings.tomraConfig.clientId || '',
        apiBaseUrl: settings.tomraConfig.apiBaseUrl || '',
        realm:      settings.tomraConfig.realm || ''
        // clientSecret is not returned by GET — leave blank
      }});
      this.tomraVouchers = Array.isArray(settings.tomraConfig.vouchers)
        ? settings.tomraConfig.vouchers.map((v: any) => ({ plu: v.plu || '', name: v.name || '' }))
        : [];
    } else {
      this.storeSettingsForm.patchValue({ tomraConfig: { enabled: false, mode: 'offline' } });
      this.tomraVouchers = [];
    }

    // Populate Auto Z-Report config
    const zCfg = settings.autoZReportConfig;
    if (zCfg) {
      this.autoZReportEnabled = zCfg.enabled || false;
      this.autoZReportScheduledTime = zCfg.scheduledTime || '23:00';
      this.autoZReportLastRunAt = zCfg.lastRunAt ? new Date(zCfg.lastRunAt) : null;
      this.autoZReportLastRunStatus = zCfg.lastRunStatus || null;
      this.autoZReportLastRunError = zCfg.lastRunError || null;
      this.autoZReportHistory = Array.isArray(zCfg.executionHistory) ? zCfg.executionHistory : [];
    } else {
      this.autoZReportEnabled = false;
      this.autoZReportScheduledTime = '23:00';
      this.autoZReportLastRunAt = null;
      this.autoZReportLastRunStatus = null;
      this.autoZReportLastRunError = null;
      this.autoZReportHistory = [];
    }
  }

  setActiveTab(tab: string): void {
    this.activeTab = tab;
  }

  saveSettings(): void {
    if (this.storeSettingsForm.invalid) {
      this.toastr.error('Please fix form errors before saving');
      return;
    }

    this.saving = true;
    this.spinner.show();

    const formValue = this.storeSettingsForm.value;
    
    const updateData = {
      storeName: formValue.storeName,
      currency: formValue.currency,
      commissionRate: formValue.commissionRate,
      payoutSchedule: formValue.payoutSchedule,
      isEcommerceEnabled: formValue.isEcommerceEnabled,
      inventoryManagementEnabled: formValue.inventoryManagementEnabled,
      skuRequirementEnabled: formValue.skuRequirementEnabled,
      catalogImportDefaultCategoryId: formValue.catalogImportDefaultCategoryId || null,
      bankDetails: {
        accountNumber: formValue.bankAccountNumber,
        bankName: formValue.bankName,
        swiftCode: formValue.swiftCode,
        accountHolderName: formValue.bankAccountHolderName
      },
      taxInfo: {
        vatNumber: formValue.vatNumber,
        taxId: formValue.taxId,
        businessRegistration: formValue.businessRegistration
      },
      depositSettings: {
        enabled: formValue.depositEnabled,
        autoAssignByEAN: formValue.depositAutoAssignByEAN,
        feeTypes: this.storeFeeTypeEntries.map((e: any) => ({
          feeTypeId: e.feeTypeId,
          amount: e.amount,
          enabled: e.enabled
        })),
        // Legacy backward-compat fields
        categoryA: formValue.depositCategoryA,
        categoryB: formValue.depositCategoryB,
        categoryC: formValue.depositCategoryC
      }
    };

    // Include breeceConfig; only send clientSecret when user typed a new value
    const breeceForm = this.storeSettingsForm.get('breeceConfig')?.value;
    if (breeceForm) {
      if (!this.validateBreeceInstallationId(breeceForm.enabled, breeceForm.installationId)) {
        this.saving = false;
        this.spinner.hide();
        return;
      }
      (updateData as any).breeceConfig = {
        enabled: breeceForm.enabled,
        clientId: breeceForm.clientId,
        installationId: this.normalizeInstallationId(breeceForm.installationId)
      };
      if (breeceForm.clientSecret && breeceForm.clientSecret.trim() !== '') {
        (updateData as any).breeceConfig.clientSecret = breeceForm.clientSecret;
      }
    }

    // Include tomraConfig
    const tomraForm = this.storeSettingsForm.get('tomraConfig')?.value;
    if (tomraForm) {
      const tomraPayload: any = {
        enabled:    tomraForm.enabled,
        mode:       tomraForm.mode || 'offline',
        clientId:   tomraForm.clientId || '',
        apiBaseUrl: tomraForm.apiBaseUrl || '',
        realm:      tomraForm.realm || '',
        vouchers:   this.tomraVouchers.map((v: any) => ({ plu: v.plu, name: v.name }))
      };
      if (tomraForm.clientSecret && tomraForm.clientSecret.trim() !== '') {
        tomraPayload.clientSecret = tomraForm.clientSecret;
      }
      (updateData as any).tomraConfig = tomraPayload;
    }

    this.ecommerceService.updateStoreSettings(this.storeId, updateData).subscribe(
      (res: any) => {
        this.saving = false;
        this.spinner.hide();

        if (res.code === 200 || (res.response && res.response.success)) {
          this.toastr.success('Store settings updated successfully');
          this.storeSettingsForm.markAsPristine();
          this.hasUnsavedChanges = false;
          
          // Reload settings to get updated data
          this.loadStoreSettings();
        } else {
          this.toastr.error('Failed to update store settings');
        }
      },
      (error: any) => {
        this.saving = false;
        this.spinner.hide();
        console.error('Error updating store settings:', error);
        
        const msg = error?.error?.response?.message || error?.error?.message;
        this.toastr.error(msg || 'Failed to update store settings');
      }
    );
  }

  resetForm(): void {
    this.populateForm(this.storeSettings);
  }

  getCurrencySymbol(currencyCode: string): string {
    const currency = this.currencyOptions.find(c => c.value === currencyCode);
    return currency?.symbol || currencyCode;
  }

  isSavedCatalogImportCategoryMissing(): boolean {
    if (!this.storeCategoriesLoaded) {
      return false;
    }
    const id = this.storeSettingsForm.get('catalogImportDefaultCategoryId')?.value;
    if (!id) {
      return false;
    }
    return !this.storeCategories.some((c: any) => c._id === id);
  }

  get activeStoreCategoriesForMapping(): any[] {
    return this.storeCategories.filter((c: any) => c.status !== 0 && c.status !== 2);
  }

  private applyGlobalCategoryMappingLoad(globalCategoriesRes: any, globalMappingsRes: any): void {
    this.globalCategoryMappingLoadError = null;
    if (globalCategoriesRes === null && globalMappingsRes === null) {
      this.globalCategoryMappingLoadError = 'Could not load global category mapping data.';
      this.globalCategoriesFlat = [];
      this.globalCategoryMappingRows = [];
      return;
    }
    if (globalCategoriesRes === null) {
      this.globalCategoryMappingLoadError = 'Could not load global categories.';
    }
    if (globalMappingsRes === null) {
      this.globalCategoryMappingLoadError = this.globalCategoryMappingLoadError
        ? 'Could not load global categories or mappings.'
        : 'Could not load saved mappings.';
    }
    const listData = globalCategoriesRes
      ? this.globalCategoryService.extractListData(globalCategoriesRes)
      : { categories: [], tree: [] };
    this.globalCategoriesFlat = listData.categories;
    const mappings = globalMappingsRes
      ? this.globalCategoryService.extractMappings(globalMappingsRes)
      : [];
    const byGlobal = new Map<string, string>();
    for (const m of mappings) {
      if (m.globalCategoryId && m.storeCategoryId) {
        byGlobal.set(String(m.globalCategoryId), String(m.storeCategoryId));
      }
    }
    this.globalCategoryMappingRows = this.globalCategoriesFlat.map((gc: any) => ({
      globalCategoryId: gc._id,
      name: gc.name,
      depth: gc.depth ?? 0,
      path: gc.path || gc.name,
      storeCategoryId: byGlobal.get(String(gc._id)) || ''
    }));
  }

  matchGlobalCategoriesByName(): void {
    let filled = 0;
    for (const row of this.globalCategoryMappingRows) {
      if (row.storeCategoryId) {
        continue;
      }
      const target = (row.name || '').trim().toLowerCase();
      if (!target) {
        continue;
      }
      const matches = this.activeStoreCategoriesForMapping.filter(
        (sc: any) => (sc.name || '').trim().toLowerCase() === target
      );
      if (matches.length === 1) {
        row.storeCategoryId = matches[0]._id;
        filled++;
      }
    }
    if (filled > 0) {
      this.toastr.info(`Matched ${filled} row(s) by name. Click Save mappings to persist.`);
    } else {
      this.toastr.info('No unmapped rows matched exactly one store category by name.');
    }
  }

  saveGlobalCategoryMappings(): void {
    if (!this.storeId) {
      return;
    }
    this.globalCategoryMappingSaving = true;
    const mappings = this.globalCategoryMappingRows.map((row) => ({
      globalCategoryId: row.globalCategoryId,
      storeCategoryId: row.storeCategoryId || ''
    }));
    this.globalCategoryService.putStoreMappings(this.storeId, mappings).subscribe({
      next: (res: any) => {
        this.globalCategoryMappingSaving = false;
        this.toastr.success('Global category mappings saved');
        const saved = this.globalCategoryService.extractMappings(res);
        const byGlobal = new Map<string, string>();
        for (const m of saved) {
          if (m.globalCategoryId && m.storeCategoryId) {
            byGlobal.set(String(m.globalCategoryId), String(m.storeCategoryId));
          }
        }
        for (const row of this.globalCategoryMappingRows) {
          row.storeCategoryId = byGlobal.get(String(row.globalCategoryId)) || '';
        }
      },
      error: (err: any) => {
        this.globalCategoryMappingSaving = false;
        this.toastr.error(err?.error?.response?.message || 'Failed to save mappings');
      }
    });
  }

  getCatalogImportDefaultCategorySummaryName(): string {
    const id = this.storeSettingsForm.get('catalogImportDefaultCategoryId')?.value;
    if (!id) {
      return 'None';
    }
    if (!this.storeCategoriesLoaded) {
      return 'Set (categories unavailable)';
    }
    const cat = this.storeCategories.find((c: any) => c._id === id);
    return cat?.name || 'Missing category';
  }

  // Helper methods for deposit settings
  formatDepositAmount(amount: number): string {
    const currencyCode = this.storeSettingsForm.get('currency')?.value || 'DKK';
    const symbol = this.getCurrencySymbol(currencyCode);
    
    // Format based on currency
    if (currencyCode === 'USD' || currencyCode === 'EUR' || currencyCode === 'GBP') {
      return `${symbol}${amount.toFixed(2)}`;
    } else {
      return `${amount.toFixed(2)} ${symbol}`;
    }
  }

  resetDepositDefaults(): void {
    const currencyCode = this.storeSettingsForm.get('currency')?.value || 'DKK';
    
    // Set default amounts based on currency
    let defaults = { categoryA: 1.00, categoryB: 1.50, categoryC: 3.00 };
    
    if (currencyCode === 'EUR') {
      defaults = { categoryA: 0.25, categoryB: 0.25, categoryC: 0.50 };
    } else if (currencyCode === 'USD') {
      defaults = { categoryA: 0.05, categoryB: 0.10, categoryC: 0.25 };
    }
    
    this.storeSettingsForm.patchValue({
      depositCategoryA: defaults.categoryA,
      depositCategoryB: defaults.categoryB,
      depositCategoryC: defaults.categoryC
    });
  }

  /** Coerce installation ID to a finite number, or null when empty/invalid. */
  private normalizeInstallationId(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  /** Block save when Breece is enabled but installation ID is missing or invalid. */
  private validateBreeceInstallationId(enabled: boolean, installationId: unknown): boolean {
    if (!enabled) {
      return true;
    }
    if (this.normalizeInstallationId(installationId) === null) {
      this.toastr.warning('Please enter a Breece Installation ID before saving');
      return false;
    }
    return true;
  }

  testBreeceConnection(): void {
    const { clientId, clientSecret } = this.storeSettingsForm.get('breeceConfig')?.value;
    if (!clientId || !clientSecret) {
      this.toastr.warning('Please enter Client ID and Client Secret first');
      return;
    }
    this.testingBreece = true;
    this.breeceTestResult = null;
    const seq = this.storeContextSeq;
    this.breeceService.testConnection(this.storeId, clientId, clientSecret).subscribe({
      next: (res: any) => {
        if (seq !== this.storeContextSeq) {
          return;
        }
        this.testingBreece = false;
        const installations = res?.response?.data?.installations || res?.data?.installations || [];
        this.breeceInstallations = installations;
        if (installations.length === 1) {
          this.storeSettingsForm.get('breeceConfig.installationId')?.setValue(installations[0].id);
        }
        this.breeceTestResult = { success: true, message: `Connection successful. ${installations.length} installation(s) found.` };
        this.toastr.success('Breece connection successful');
      },
      error: (err: any) => {
        if (seq !== this.storeContextSeq) {
          return;
        }
        this.testingBreece = false;
        this.breeceTestResult = { success: false, message: err?.error?.response?.message || 'Connection failed' };
        this.toastr.error('Breece connection failed');
      }
    });
  }

  saveBreeceSettings(): void {
    const breeceForm = this.storeSettingsForm.get('breeceConfig');
    const enabled = breeceForm?.get('enabled')?.value ?? false;
    const installationId = breeceForm?.get('installationId')?.value;

    if (!this.validateBreeceInstallationId(enabled, installationId)) {
      return;
    }

    const updateData: any = {
      breeceConfig: {
        enabled,
        clientId: breeceForm?.get('clientId')?.value || '',
        installationId: this.normalizeInstallationId(installationId)
      }
    };
    const secret = breeceForm?.get('clientSecret')?.value;
    if (secret) {
      updateData.breeceConfig.clientSecret = secret;
    }
    this.breeceSaving = true;
    this.ecommerceService.updateStoreSettings(this.storeId, updateData).subscribe({
      next: () => {
        this.breeceSaving = false;
        this.toastr.success('Breece settings saved');
      },
      error: (err: any) => {
        this.breeceSaving = false;
        this.toastr.error(err?.error?.response?.message || err?.error?.message || 'Failed to save Breece settings');
      }
    });
  }

  saveBreeceInterval(): void {
    const syncIntervalMinutes = this.storeSettingsForm.get('breeceConfig.syncIntervalMinutes')?.value ?? 10;
    this.breeceIntervalSaving = true;
    this.breeceService.updateBreeceConfig(this.storeId, { syncIntervalMinutes }).subscribe({
      next: () => {
        this.breeceIntervalSaving = false;
        this.toastr.success('Sync interval saved');
      },
      error: (err: any) => {
        this.breeceIntervalSaving = false;
        this.toastr.error(err?.error?.response?.message || 'Failed to save sync interval');
      }
    });
  }

  triggerManualBatchSync(): void {
    this.breeceManualSyncing = true;
    this.breeceService.manualBatchSync(this.storeId).subscribe({
      next: () => {
        this.breeceManualSyncing = false;
        this.toastr.success('Manual batch sync started successfully');
      },
      error: (err: any) => {
        this.breeceManualSyncing = false;
        this.toastr.error(err?.error?.response?.message || 'Failed to trigger manual batch sync');
      }
    });
  }

  saveAutoZReportSettings(): void {
    this.autoZReportSaving = true;
    const updateData: any = {
      autoZReportConfig: {
        enabled: this.autoZReportEnabled,
        scheduledTime: this.autoZReportScheduledTime
      }
    };
    this.ecommerceService.updateStoreSettings(this.storeId, updateData).subscribe({
      next: () => {
        this.autoZReportSaving = false;
        this.toastr.success('Auto Z-Report settings saved');
        this.loadStoreSettings();
      },
      error: (err: any) => {
        this.autoZReportSaving = false;
        this.toastr.error(err?.error?.response?.message || err?.error?.message || 'Failed to save Auto Z-Report settings');
      }
    });
  }

  addTomraVoucher(): void {
    this.tomraVouchers.push({ plu: '', name: 'TOMRA Flaskebon' });
  }

  removeTomraVoucher(index: number): void {
    this.tomraVouchers.splice(index, 1);
  }

  saveTomraSettings(): void {
    const tomraForm = this.storeSettingsForm.get('tomraConfig')?.value;
    this.tomraSaving = true;
    const tomraPayload: any = {
      enabled:    tomraForm?.enabled ?? false,
      mode:       tomraForm?.mode || 'offline',
      clientId:   tomraForm?.clientId || '',
      apiBaseUrl: tomraForm?.apiBaseUrl || '',
      realm:      tomraForm?.realm || '',
      vouchers:   this.tomraVouchers.map((v: any) => ({ plu: v.plu, name: v.name }))
    };
    // Only send clientSecret when the user typed a new value
    if (tomraForm?.clientSecret && tomraForm.clientSecret.trim() !== '') {
      tomraPayload.clientSecret = tomraForm.clientSecret;
    }
    const updateData: any = { tomraConfig: tomraPayload };
    this.ecommerceService.updateStoreSettings(this.storeId, updateData).subscribe({
      next: () => {
        this.tomraSaving = false;
        this.toastr.success('TOMRA settings saved');
      },
      error: (err: any) => {
        this.tomraSaving = false;
        this.toastr.error(err?.error?.response?.message || err?.error?.message || 'Failed to save TOMRA settings');
      }
    });
  }

  async testTomraConnection(): Promise<void> {
    this.tomraConnectionStatus = null;
    this.tomraConnectionLoading = true;
    const seq = this.storeContextSeq;
    try {
      await this.ecommerceService.testTomraConnection(this.storeId).toPromise();
      if (seq !== this.storeContextSeq) {
        return;
      }
      this.tomraConnectionStatus = 'success';
    } catch (err: any) {
      if (seq !== this.storeContextSeq) {
        return;
      }
      const msg = err?.error?.message || err?.message || 'Unknown error';
      this.tomraConnectionStatus = 'error:' + msg;
    } finally {
      if (seq === this.storeContextSeq) {
        this.tomraConnectionLoading = false;
      }
    }
  }
}